import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Custom hook for case selection logic in AI Workspace
 * Handles fetching cases, filtering, recent cases, and localStorage persistence
 */
export const useCaseSelection = () => {
  const navigate = useNavigate();
  
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [recentCases, setRecentCases] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCases, setFilteredCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCases = useCallback(async () => {
    try {
      const token = auth.getToken();
      
      if (!token) {
        console.error('❌ No auth token found');
        setLoading(false);
        toast.error('Du må logge inn på nytt');
        navigate('/login');
        return;
      }
      
      const response = await axios.get(`${API}/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const casesList = response.data || [];
      setCases(casesList);
      
      // Load recent cases from localStorage
      const storedRecent = localStorage.getItem('ai_workspace_recent_cases');
      if (storedRecent) {
        try {
          const recentIds = JSON.parse(storedRecent);
          const recentCasesList = casesList.filter(c => recentIds.includes(c.id));
          setRecentCases(recentCasesList.slice(0, 5));
        } catch (e) {
          console.error('Failed to parse recent cases:', e);
        }
      }
      
      // Try to restore previously selected case from localStorage
      const storedCaseId = localStorage.getItem('ai_workspace_active_case');
      if (storedCaseId) {
        const restoredCase = casesList.find(c => c.id === storedCaseId);
        if (restoredCase) {
          setSelectedCase(restoredCase);
        }
      }
    } catch (error) {
      console.error('❌ Failed to fetch cases:', error);
      
      if (error.response?.status === 401) {
        toast.error('Økt utløpt. Vennligst logg inn på nytt.');
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error('Du har ikke tilgang til denne funksjonen');
      } else {
        toast.error('Kunne ikke laste saker. Prøv å oppdatere siden.');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Filter cases based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCases(cases);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = cases.filter(c => 
      (c.title && c.title.toLowerCase().includes(query)) ||
      (c.topic && c.topic.toLowerCase().includes(query)) ||
      (c.client && c.client.toLowerCase().includes(query))
    );
    setFilteredCases(filtered);
  }, [searchQuery, cases]);

  // Select a case and update localStorage
  const selectCase = useCallback((caseObj) => {
    // Allow clearing the selection
    if (caseObj === null) {
      localStorage.removeItem('ai_workspace_active_case');
      setSelectedCase(null);
      return;
    }
    
    // Store in localStorage
    localStorage.setItem('ai_workspace_active_case', caseObj.id);
    
    // Update recent cases
    const storedRecent = localStorage.getItem('ai_workspace_recent_cases');
    let recentIds = [];
    if (storedRecent) {
      try {
        recentIds = JSON.parse(storedRecent);
      } catch (e) {
        console.warn('Failed to parse recent cases from localStorage:', e);
      }
    }
    
    // Add current case to front, remove duplicates, keep max 5
    recentIds = [caseObj.id, ...recentIds.filter(id => id !== caseObj.id)].slice(0, 5);
    localStorage.setItem('ai_workspace_recent_cases', JSON.stringify(recentIds));
    
    // Update state
    setSelectedCase(caseObj);
    setRecentCases(prev => {
      const updated = [caseObj, ...prev.filter(c => c.id !== caseObj.id)].slice(0, 5);
      return updated;
    });
  }, []);

  return {
    cases,
    selectedCase,
    recentCases,
    searchQuery,
    setSearchQuery,
    filteredCases,
    loading,
    fetchCases,
    selectCase
  };
};

export default useCaseSelection;
